import {Vector} from './Vector';
import {Shape} from './interfaces/shape';
import {p5} from './runner-idle';
import Decimal from './break_infinity';

type ShapeLayerType = 'UNION' | 'INTERSECT';

export class ShapeLayer {
  shape: Shape;
  type: ShapeLayerType;
  tag: string;
  constructor(
    shape: Shape,
    {type = 'UNION', tag = ''}: {type?: ShapeLayerType; tag?: string}
  ) {
    this.shape = shape;
    this.type = type;
    this.tag = tag;
  }
}
export class CompositeShape implements Shape {
  /** The amount the tags of this Region extend beyond its borders. */
  tagRange: Decimal = new Decimal(0);
  /** Is this point in the axially-aligned bounding box for this Region? */
  inAABB(point: Vector): boolean {
    return (
      point.x.gte(this.leftBound) &&
      point.x.lte(this.rightBound) &&
      point.y.gte(this.bottomBound) &&
      point.y.lte(this.topBound)
    );
  }

  /** Is this point outside the axially-aligned bounding box for this Region? */
  outAABB(point: Vector): boolean {
    return (
      point.x.lt(this.leftBound) ||
      point.x.gt(this.rightBound) ||
      point.y.lt(this.bottomBound) ||
      point.y.gt(this.topBound)
    );
  }
  hasTagsAt(point: Vector): boolean {
    if (this.outAABB(point)) return false;
    return (
      this.contains(point) ||
      this.nearestPointTo(point).distance(point).lte(this.tagRange)
    );
  }
  distance(point: Vector): Decimal {
    return this.nearestPointTo(point).distance(point);
  }
  nearestPointTo(point: Vector): Vector {
    return this.shapes
      .reduce((acc, shapeLayer) =>
        acc.shape.distance(point).lt(shapeLayer.shape.distance(point))
          ? acc
          : shapeLayer
      )
      .shape.nearestPointTo(point);
  }
  contains(point: Vector): boolean {
    return this.shapes.reduce(
      (acc, shapeLayer) =>
        acc ||
        shapeLayer.shape.contains(point) != (shapeLayer.type === 'INTERSECT'),
      false
    );
  }
  draw(p: p5, zoomScale: Decimal) {
    p.push();
    this.shapes.forEach((shapeLayer) => {
      p.push();
      if (shapeLayer.type === 'INTERSECT') {
        p.blendMode(p.REMOVE);
      }
      shapeLayer.shape.draw(p, zoomScale);
      p.pop();
    });
    p.pop();
  }
  shapes: ShapeLayer[];
  leftBound: Decimal = Decimal.POSITIVE_INFINITY;
  rightBound: Decimal = Decimal.NEGATIVE_INFINITY;
  topBound: Decimal = Decimal.NEGATIVE_INFINITY;
  bottomBound: Decimal = Decimal.POSITIVE_INFINITY;
  constructor({tagRange = new Decimal(0), shapes = [] as ShapeLayer[]}) {
    this.tagRange = tagRange;
    this.shapes = shapes;
    this.shapes.forEach(({shape}) => {
      this.updateBounds(shape);
    });
  }
  toString() {
    return this.shapes
      .map((shapeLayer) => shapeLayer.shape.toString())
      .join(' & ');
  }

  updateBounds(shape: Shape) {
    this.leftBound = shape.leftBound.min(this.leftBound);
    this.rightBound = shape.rightBound.max(this.rightBound);
    this.topBound = shape.topBound.max(this.topBound);
    this.bottomBound = shape.bottomBound.min(this.bottomBound);
  }

  addShape(
    shape: Shape,
    {type, tag} = {type: 'UNION' as const, tag: ''}
  ): CompositeShape {
    this.shapes.push(new ShapeLayer(shape, {type}));
    this.updateBounds(shape);
    return this;
  }

  removeShape(tag = '') {
    if (tag) {
      const oldSize = this.shapes.length;
      this.shapes = this.shapes.filter((shape) => shape.tag === tag);
      if (this.shapes.length != oldSize) {
        // Recalculate AABB
        this.leftBound = Decimal.POSITIVE_INFINITY;
        this.rightBound = Decimal.NEGATIVE_INFINITY;
        this.topBound = Decimal.NEGATIVE_INFINITY;
        this.bottomBound = Decimal.POSITIVE_INFINITY;
        this.shapes.forEach(({shape}) => {
          this.updateBounds(shape);
        });
      }
    } else {
      this.shapes.pop();
    }
  }
}
